<template>
	<component
		:is="PageComponent"
		v-if="PageComponent"
		:slug="slug"
		:unique-product-name="slug"
		:localized-seo="data?.seo"
	/>
</template>

<script setup lang="ts">
import { markRaw, defineAsyncComponent } from "vue";
import type { Component } from "vue";

const route = useRoute();
const { resolveSlug } = useNorcePwa();
const { localeProperties } = useI18n();
const { setSuppressI18nHead } = usePageStore();

const rawSlug = Array.isArray(route.params.slug)
	? route.params.slug.join("/")
	: route.params.slug || "";
const slug = encodeURIComponent(rawSlug);

// Clear before await — prevents stale suppressI18nHead from a previous
// product page carrying over into the next navigation.
setSuppressI18nHead(false);

const { data, error } = await useAsyncData(
	`resolveSlug-${slug}`,
	() =>
		resolveSlug({
			slug: slug as string,
			cultureCode: localeProperties.value.iso as string
		}),
	{
		lazy: false
	}
);
const type = (data.value?.type as string) || "";
if (error.value) {
	console.error("Error resolving slug:", error.value);
}

if (type === "redirect") {
	const targetPath = data.value?.targetPath as string;
	const redirectCode = (data.value?.redirectType as number) || 302;

	navigateTo(targetPath, { redirectCode: redirectCode || 302 });
}

let PageComponent: Component | null = null;
if (type === "product") {
	setSuppressI18nHead(true);
	PageComponent = markRaw(
		defineAsyncComponent(
			() => import("./product/[uniqueProductName]/index.vue")
		)
	);
} else if (type === "category") {
	setSuppressI18nHead(true);
	PageComponent = markRaw(
		defineAsyncComponent(() => import("./category/[...slug].vue"))
	);
} else {
	PageComponent = markRaw(
		defineAsyncComponent(() => import("./cms/[...slug].vue"))
	);
}
</script>
